import {useCallback, useRef} from 'react'
import {getAccessToken} from '../helper/authHelper'
import {loginRequest} from '../auth/auth-config'
import {useIsAuthenticated, useMsal} from '@azure/msal-react'
import {useDispatch, useSelector} from 'react-redux'
import {setCancellation, setSessionId} from '../store/askGivePrism/chatbot/chatbotStreamSlice'

const URL =
  process.env.NODE_ENV === 'production' ? globalThis.__env.REACT_APP_API_CHAT_URL : process.env.REACT_APP_API_CHAT_URL

export const useSessionManager = () => {
  const {instance, accounts, inProgress} = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const appName = 'ask_giveprism'
  const userId = useSelector((state) => state.userProfile.email)
  const dispatch = useDispatch()
  const abortControllerRef = useRef(null)
  const azureRole = useSelector((state) => state.superUserTenantSlice.azureRole)
  const superUser = useSelector((state) => state.superUserTenantSlice.isSuperUser)

  const createSession = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort() // Abort any ongoing request
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    try {
      const accessToken = await getAccessToken(instance, accounts, inProgress)
      if (!isAuthenticated) {
        instance.loginRedirect(loginRequest)
        return null
      }

      const createSessionURL = superUser
        ? `${URL}/apps/${appName}/users/${userId}/role/${azureRole}/sessions`
        : `${URL}/apps/${appName}/users/${userId}/sessions`

      const responseSession = await fetch(createSessionURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        signal: abortController.signal
      })

      if (!responseSession.ok) {
        throw new Error('Failed to create session')
      }

      const sessionData = await responseSession.json()
      dispatch(setSessionId(sessionData.id))
      return sessionData.id
    } finally {
      abortControllerRef.current = null // Clear the AbortController
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, inProgress, isAuthenticated, userId, dispatch, superUser, azureRole])

  const deleteSession = useCallback(
    async (sessionId) => {
      if (!sessionId) {
        return true
      }

      const deleteSessionURL = superUser
        ? `${URL}/apps/${appName}/users/${userId}/role/${azureRole}/sessions/${sessionId}`
        : `${URL}/apps/${appName}/users/${userId}/sessions/${sessionId}`

      try {
        const accessToken = await getAccessToken(instance, accounts, inProgress)
        const response = await fetch(deleteSessionURL, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to delete session: ${response.statusText}`)
        }

        await response.json()
        dispatch(setSessionId(null))
      } catch (error) {
        console.error('Session deletion failed:', error)
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, accounts, inProgress, userId, dispatch, superUser, azureRole]
  )

  const sendMessage = useCallback(
    async (sessionId, message) => {
      if (!sessionId || !isAuthenticated) {
        console.error('Cannot send message: missing sessionId or not authenticated')
        return null
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort() // Abort any ongoing request
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      try {
        const accessToken = await getAccessToken(instance, accounts, inProgress)
        const requestBody = JSON.stringify({
          app_name: appName,
          user_id: userId,
          session_id: sessionId,
          azure_role: azureRole,
          new_message: {
            role: 'user',
            parts: [
              {
                text: message
              }
            ]
          },
          streaming: true
        })

        const responseRunSse = await fetch(`${URL}/run_sse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: requestBody,
          signal: abortController.signal
        })

        if (!responseRunSse.ok) {
          throw new Error(`Failed to send message: ${responseRunSse.statusText}`)
        }
        return responseRunSse.body
      } catch (error) {
        if (error.name === 'AbortError') {
          return null
        } else {
          throw error
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appName, userId, instance, accounts, inProgress, isAuthenticated, superUser, azureRole]
  )

  const cancelProcess = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort() // Abort the ongoing request
    }
    dispatch(setCancellation(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cancelResponse = useCallback(
    async (responseId) => {
      if (!responseId || !isAuthenticated) {
        return true
      }

      const cancelURL = superUser ? `${URL}/cancel/${responseId}/role/${azureRole}` : `${URL}/cancel/${responseId}`
      try {
        const accessToken = await getAccessToken(instance, accounts, inProgress)
        const response = await fetch(cancelURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        })

        if (!response.ok) {
          if (response.status === 404 || response.status === 400) {
            return null
          }
          throw new Error(`Failed to cancel response: ${response.statusText}`)
        }

        return response.json()
      } catch (error) {
        console.error('Cancellation Response failed:', error)
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, accounts, inProgress, isAuthenticated, superUser, azureRole]
  )

  return {
    createSession,
    deleteSession,
    sendMessage,
    cancelResponse,
    cancelProcess
  }
}

export default useSessionManager
